import Layout from '@/components/layout/Layout';
import { FC } from 'react';
import Link from 'next/link';
import BookCardItem from '@/components/ui/book-card-item/BookCardItem';
import Pagination from '@/components/ui/pagination/Pagination';
import PageHeadingSort from '@/components/ui/page-heading-sort/PageHeadingSort';
import Breadcrumbs from '@/components/ui/breadcrumbs/Breadcrumbs';
import SectionTitleMenu from '@/components/ui/section-title-menu/SectionTitleMenu';
import PopularCustomSlider from '@/components/ui/popular-custom-slider/PopularCustomSlider';
import SimilarAuthorsList from '@/components/ui/similar-authors/SimilarAuthorsList';
import { BOOKS_FOR_PAGE } from '@/helpers/variables';
import { getAuthorFullName } from '@/helpers/helpers';
import { IAvthorDetail, IAvthorBookData } from '@/interfaces/avthorDetail.interface';
import { useGetUrlPath } from '@/hooks/useGetUrlPath';
import { IPopularBooksCustomData } from '@/interfaces/popularBooksCustom.interface';
import { ISimilarAuthors } from '@/interfaces/similarAuthors';

import { LITRES_AUTHORS } from '@/helpers/variables';
import styles from './AvthorDetail.module.scss';

interface IAvthorDetailProps {
  books: IAvthorDetail;
  sortingName: string;
  sortingUrl?: string | undefined;
  similarAuthors: ISimilarAuthors;
}

const AvthorDetail: FC<IAvthorDetailProps> = ({ books, sortingName, sortingUrl, similarAuthors }) => {
  const pagesCount = Math.ceil(books.books.total_count / BOOKS_FOR_PAGE);

  const authorData = books.base_author_data;
  const authorFullName = getAuthorFullName(authorData.first_name, authorData.middle_name, authorData.last_name);

  const SEO_TITLE = `${authorFullName} скачать бесплатно все книги в txt, pdf, DjVu, rtf или читать онлайн`;
  const SEO_DESCRIPTION = `${authorFullName} скачать бесплатно все книги в txt, pdf, DjVu, rtf или читать онлайн в электронной библиотеке КучаКниг.`;
  const SEO_CANONICAL = `/avtor/${books.alias}/`;

  const getBookBreadcrumbs = () => {
    return {
      __html: `{
      "@context": "https://schema.org/",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {"@type": "ListItem","position": 1,"name": "Главная","item": "https://kuchaknig.org/"},
        {"@type": "ListItem","position": 2,"name": "Aвторы","item": "https://kuchaknig.org/avtor/"},
        {"@type": "ListItem","position": 3,"name": "${authorFullName}","item": "https://kuchaknig.org/avtor/${books.alias}/"}
       ]
    }`,
    };
  };

  const isLitresAuthor = LITRES_AUTHORS.some((item) => item === books.alias);

  return (
    <Layout title={SEO_TITLE} description={SEO_DESCRIPTION} canonicalHref={SEO_CANONICAL} pageHref={useGetUrlPath()}>
      <Breadcrumbs pageName="Aвторы" pageAlias="/avtor/" dynamicPageName={`${authorFullName}`} />
      <script type="application/ld+json" dangerouslySetInnerHTML={getBookBreadcrumbs()} key="product-jsonld" />

      <SectionTitleMenu extraClassName={'sectionTitleMenu_sort'}>
        <PageHeadingSort titleName={`Книги автора: ${authorFullName}`} sortingName={sortingName} url={`/avtor/${books.alias}/`} extraClassName="pageHeading_plain" />
      </SectionTitleMenu>

      {isLitresAuthor && <Link prefetch={false} className={styles.litresAuthorLink} href={`https://www.litres.ru/author/${books.alias}/`} target="_blank">{`${books.base_author_data.first_name} ${books.base_author_data.last_name} книги`}</Link>}

      <div data-cards="list" className="cardsWrapper">
        {books.books.data.length ? (
          books.books.data.map((book: IAvthorBookData) => (
            <BookCardItem
              authorAlias={books.alias}
              createdAt={book.satellite_book.created_at}
              alias={book.satellite_book.alias}
              author={authorData}
              book={book}
              key={book.id}
              rating={book.satellite_book.rating}
              draft={book.draft}
              isAuthorsPage
              readOnline
            />
          ))
        ) : (
          <div>Книги не найдены</div>
        )}
      </div>
      {!!sortingUrl ? <Pagination pagesCount={pagesCount ?? 0} postName={`avtor/${books.alias}/${sortingUrl}/page=`} /> : <Pagination pagesCount={pagesCount ?? 0} postName={`avtor/${books.alias}/pages/page=`} />}

      {similarAuthors && similarAuthors.length > 0 && <SimilarAuthorsList similarAuthors={similarAuthors} />}

      {/* <Breadcrumbs pageName="Aвторы" pageAlias="/avtor/" isBottom={true} dynamicPageName={`${authorFullName}`} /> */}
    </Layout>
  );
};

export default AvthorDetail;
